import { useState, useSyncExternalStore } from 'react';
import { useChatStore } from '../../../store/chatStore';
import type { ChatMessageEvent } from '../../../types/chat';
import { isSystemMessage } from '../../../lib/chat-message';
import { Bot, Check, Copy, MoreVertical, Reply, Smile, Trash2 } from '../../../icons';
import { AiTextMessage } from './AiTextMessage';
import { AiActionCard } from './AiActionCard';
import { AiConfirmChatCard } from './AiConfirmChatCard';
import { readChatConfirmCard } from '../../../api/chat-ai-confirm';
import { LinkPreviewCard } from './LinkPreviewCard';
// chat-polls — 투표 메시지 자리에는 말풍선 대신 카드를 그린다.
import { PollCard } from './PollCard';
import { useChatPollStore, pollForMessage } from '../../../store/chatPollStore';
import { MarkdownPreviewTriggers } from './MarkdownPreviewTriggers';
import { AiAvatar } from '../../../components/AiAvatar';
import { AttachmentList } from '../../../components/Attachment';
import { formatMessageTime } from './utils/formatMessageTime';
import { renderMessageMarkdown } from '../../../lib/chat-markdown';
import { extractSoleUrl } from '../../../lib/link-preview';
import { ReplyQuote } from './ReplyQuote';
import { ReactionBar } from './ReactionBar';
import { EmojiPicker } from './EmojiPicker';
import { toggleReaction, deleteMessage } from '../../../api/chat';
import { layerConfirm, layerAlert } from '../../../components/LayerPopup/LayerPopup';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
// 프로필 (2026-08-31 · 시안 8/9) — 대화 중 「이 사람 누구지」가 가장 잦은데 눌러도 안 열렸다.
import { openPersonProfile } from '../../../store/personProfileStore';

interface MessageItemProps {
  message: ChatMessageEvent;
  showAvatar: boolean;
  isMe: boolean;
  sender: { name: string; jobTitle: string | null; avatarSrc?: string | null };
  /**
   * chat-read-receipts — 이 메시지를 아직 읽지 않은 인원. -1 이면 표시하지 않는다
   * (내 메시지가 아니거나 읽음 상태를 아직 모르는 경우). 계산은 MessageList 의 getReceipt.
   */
  unreadBy?: number;
  /** 전원이 읽었을 때 내 마지막 메시지에만 붙는 라벨 ('읽음' / '모두 읽음'). */
  readLabel?: string | null;
  /** 미읽음 배지 hover 툴팁 (그룹이면 안 읽은 사람 이름들). */
  readTitle?: string | null;
}

/**
 * MessageItem (P2-16c) — 시안 line 1166~1206. AI 분기 라우팅:
 *
 *   - AI 텍스트 (isAi && !aiCardType) → AiTextMessage (P2-16d) 가 row 전체 책임
 *   - AI 액션/매뉴얼 카드 (isAi && aiCardType) → row + sender 메타 + 본문 텍스트 + AiActionCard (P2-16e)
 *   - 일반 사용자/me → 기존 row + 말풍선 + hover 액션
 *
 * sender hover 액션바: Reply / Copy / Smile / More — RotateCcw 는 AI 전용이라 AiTextMessage
 * 에서만 노출. AI 카드는 hover 액션 미노출 (시안 line 1194 정책).
 */
export function MessageItem({
  message, showAvatar, isMe, sender,
  unreadBy = -1, readLabel = null, readTitle = null,
}: MessageItemProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  // hover 툴바는 CSS :hover 로 표시 — 전역 hoveredMsgId 구독 제거(hover 시 전체 메시지
  // 리렌더 → markdown/mermaid 재렌더 깜빡임 방지).
  const setReplyingTo = useChatStore((s) => s.setReplyingTo);
  const reactionOverride = useChatStore((s) => s.reactionsByMessageId[message.id]);
  const applyMyReaction = useChatStore((s) => s.applyMyReaction);
  const markMessageDeleted = useChatStore((s) => s.markMessageDeleted);
  const reactions = reactionOverride ?? message.reactions ?? [];
  const [copied, setCopied] = useState(false);
  const [showReactPicker, setShowReactPicker] = useState(false);
  // 발신자 사진 로드 실패(만료·고아 객체) 시 기존 이니셜 폴백 (2026-06-10 additive).
  const [avatarError, setAvatarError] = useState(false);
  /**
   * ★★ **아바타·보낸이 이름을 누르면 프로필이 열린다** (2026-08-31 · 시안 8/9).
   *    종전에는 아무 일도 안 했다 — 조직도와 참여자 목록에서만 사람 카드를 열 수 있었다.
   *    ★ AI·시스템 메시지에는 열 사람이 없다(`senderId` 가 없다) — 그때는 종전처럼 그린다.
   *    ★ 여는 것은 **조직도·참여자와 같은 팝업**이다. 무엇이 보이나는 서버가 정한다.
   */
  const senderId = message.senderId == null ? null : String(message.senderId);
  const openProfile = senderId
    ? () => openPersonProfile(senderId, { name: sender.name, avatarUrl: sender.avatarSrc ?? null, position: sender.jobTitle })
    : null;

  const handleCopy = () => {
    void navigator.clipboard?.writeText(message.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const handleReact = async (emoji: string) => {
    setShowReactPicker(false);
    try {
      const res = await toggleReaction(message.roomId, message.id, emoji);
      applyMyReaction(message.id, reactions, emoji, res.action);
    } catch { /* best-effort — 실패 시 다음 getMessages 로 회복 */ }
  };
  // chat-message-delete — 본인 메시지 전체 회수. 확인 후 삭제 → optimistic 톰스톤(SSE 도 동일·멱등).
  const handleDelete = async () => {
    const ok = await layerConfirm(tChat('chat.item.delete_confirm'));
    if (!ok) return;
    try {
      await deleteMessage(message.roomId, message.id);
      markMessageDeleted(message.roomId, message.id);
    } catch {
      void layerAlert(tChat('chat.item.delete_failed'));
    }
  };
  // chat-polls — 이 메시지가 투표 카드 자리인지. 방의 투표 목록에서 messageId 로 찾는다.
  const roomPolls = useChatPollStore((s) => s.pollsByRoom[message.roomId]);
  const poll = pollForMessage(roomPolls, message.id);

  const isAi = message.isAi;
  // source 분기: ai > system > user. lib/chat-message.ts SSOT.
  const isSystem = isSystemMessage(message);
  // aiCardType 이 일차 신호. isManualCard 는 SSE 발행자가 둘 중 하나만 set 할 수 있으므로
  // OR 결합 — backend 가 aiCardType:null + isManualCard:true 발행해도 ManualLookup 라우팅.
  // (review F-C1 봉합)
  const isCard = isAi && (message.aiCardType === 'action' || message.aiCardType === 'manual' || message.isManualCard);
  /* 승인 카드 (2026-09-21) — 첨부 칸에 실려 온다. 카드가 없으면 `null` 이라 종전과 같다.
     ★ `isCard` 에 섞지 않는다 — 말풍선(무슨 일이 일어나나)은 그대로 보이고 카드가 아래 붙는다. */
  const confirmCard = isAi ? readChatConfirmCard(message.attachments) : null;
  const time = formatMessageTime(message.createdAt);
  // chat-message-delete — 전체 회수된 메시지(본문/첨부 블랭크). "삭제된 메시지입니다" 톰스톤 렌더.
  const isDeleted = message.isDeleted;

  // AI 비서에게 보낸 사용자 메시지 — '/ai ' prefix 자동 감지.
  // render 시점에만 prefix 슬라이스 (DB content 는 그대로). 시각적으로 AI 대화 섹션으로 분리.
  const isAiPrompt = !isAi && !isSystem && /^\/ai(\s|$)/.test(message.content);
  const displayContent = isAiPrompt ? message.content.replace(/^\/ai\s*/, '') : message.content;

  // URL-only 메시지 → 사이트 미리보기 칩으로 전환, raw URL 비표시 (chat-link-preview, additive).
  // 시스템/AI/AI-프롬프트 제외 — 일반 사용자가 보낸 "URL만 있는 메시지"에만 적용.
  const soleUrl = !isSystem && !isAi && !isAiPrompt ? extractSoleUrl(displayContent) : null;

  // 첨부만 있고 본문이 공백인 메시지(파일 업로드 시 placeholder ' ')는 빈 말풍선을 렌더하지 않는다.
  // 서버가 비어있지 않은 content 를 요구해 ' ' 가 저장되지만 시각적으로는 빈 박스라, 본문에 보일 게
  // 있을 때만 말풍선을 렌더. 첨부 카드는 아래 AttachmentList 가 그대로 표시. (additive 가드)
  const hasBubbleContent = isDeleted || isSystem || !!soleUrl || displayContent.trim().length > 0;

  /**
   * AI 텍스트는 별도 컴포넌트로 위임 (hover 에 RotateCcw 포함).
   *
   * ★★★★ **승인 카드는 여기서 새 나갔다** (2026-09-21 운영에서 육안으로 잡힘).
   *
   * `isCard` 는 옛 두 종류('action'·'manual')만 본다. 그래서 `ai_card_type='confirm'`
   * 메시지는 이 이른 `return` 에 걸려 **아래 렌더를 아예 안 탔다** — 말풍선 글만 뜨고
   * 「아래에서 **확인**을 눌러 주세요」라고 써 놓은 채 **누를 단추가 없었다.**
   *
   * 조각 검사(카드 컴포넌트)·서버 응답(첨부에 재료 있음)은 **둘 다 초록이었다.**
   * 둘 사이를 잇는 이 한 줄만 비어 있었고, 그건 **화면을 열어 봐야** 보인다.
   *
   * ★ `isCard` 에 'confirm' 을 넣지 않는다 — 그러면 아래 `AiActionCard` 가 같이 떠서
   *   「모르는 동작」 자리표시가 붙는다. 조건만 넓힌다.
   */
  if (isAi && !isCard && !confirmCard) {
    return <AiTextMessage message={message} showAvatar={showAvatar} />;
  }

  const rowClass = [
    'chat-message-row',
    showAvatar ? 'chat-message-row--show-avatar' : 'chat-message-row--continuation',
    isMe ? 'chat-message-row--me' : '',
    isSystem ? 'chat-message-row--system' : '',
    isAiPrompt ? 'chat-message-row--ai-prompt' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /* data-message-id — 대화 검색 결과에서 해당 메시지로 뛰는 앵커(ChatDetailPanel.jumpTo). */
    <div className={rowClass} data-message-id={message.id}>
      <div className="chat-message-avatar-slot">
        {showAvatar && !isAi && (() => {
          const inner = sender.avatarSrc && !avatarError ? (
            <img
              className="chat-message-avatar-img"
              src={sender.avatarSrc}
              alt=""
              loading="lazy"
              onError={() => setAvatarError(true)}
            />
          ) : (
            (sender.name ?? '?').slice(0, 1)
          );
          // 열 사람이 없으면 **종전 그대로** — 눌러도 아무 일이 없는 단추를 만들지 않는다.
          return openProfile ? (
            <button
              type="button"
              className="chat-message-avatar chat-message-avatar--btn"
              aria-label={tChat('chat.item.open_profile', { name: sender.name })}
              onClick={openProfile}
            >{inner}</button>
          ) : (
            <div className="chat-message-avatar" aria-label={sender.name}>{inner}</div>
          );
        })()}
        {showAvatar && isAi && (
          <div className="chat-message-avatar chat-message-avatar--ai" aria-label={tChat('chat.item.ai_assistant')}>
            <AiAvatar />
          </div>
        )}
      </div>

      <div className="chat-message-body">
        {showAvatar && (
          <div className="chat-message-meta">
            {isSystem || isAi || !openProfile ? (
              <span className="chat-message-sender">{isSystem ? tChat('chat.item.system') : isAi ? tChat('chat.item.ai_assistant') : sender.name}</span>
            ) : (
              <button
                type="button"
                className="chat-message-sender chat-message-sender--btn"
                aria-label={tChat('chat.item.open_profile', { name: sender.name })}
                onClick={openProfile}
              >{sender.name}</button>
            )}
            {isSystem ? (
              <span className="chat-message-system-badge" aria-label={tChat('chat.item.system_badge_aria')}>{tChat('chat.item.system')}</span>
            ) : isAi ? (
              <span className="chat-message-ai-badge">{tChat('chat.item.ai_badge')}</span>
            ) : (
              sender.jobTitle && <span className="chat-message-time">{sender.jobTitle}</span>
            )}
            {time && <span className="chat-message-time">{time}</span>}
          </div>
        )}

        {isAiPrompt && (
          <div className="chat-message-ai-prompt-chip" aria-label={tChat('chat.item.ai_prompt_label')}>
            <Bot size={11} aria-hidden />
            <span>{tChat('chat.item.ai_prompt_label')}</span>
          </div>
        )}
        {!isSystem && <ReplyQuote roomId={message.roomId} replyToId={message.replyToId} />}
        {hasBubbleContent && (
        <div className="chat-message-bubble-wrap">
          <div
            className={`chat-message-bubble${isMe ? ' chat-message-bubble--me' : ''}${
              isAi ? ' chat-message-bubble--ai' : ''
            }${isAiPrompt ? ' chat-message-bubble--ai-prompt' : ''}${soleUrl ? ' chat-message-bubble--link' : ''}${poll ? ' chat-message-bubble--poll' : ''}${isDeleted ? ' chat-message-bubble--deleted' : ''}`}
          >
            {isDeleted
              ? <span className="chat-message-deleted">{tChat('chat.item.deleted_tombstone')}</span>
              : poll
                // 투표는 카드로 — 대화에 남은 `[투표] 질문` 글자는 카드가 대신한다.
                // (투표를 모르는 화면에서는 그 글자가 그대로 보인다.)
                ? <PollCard poll={poll} canClose={poll.createdBy === message.senderId && isMe} />
                : isSystem
                ? message.content
                : soleUrl
                  ? <LinkPreviewCard url={soleUrl} />
                  : message.contentType === 'plain'
                    // '원문 그대로'로 보낸 메시지만 글자 그대로 그린다(2026-08-26). 기존 값
                    // text 는 지금처럼 마크다운으로 — 저장된 대화가 전부 text 라 그 뜻을
                    // 바꾸면 과거 대화와 AI 답변이 통째로 평문이 된다.
                    ? <span className="chat-message-plain">{displayContent}</span>
                    : renderMessageMarkdown(displayContent)}
          </div>
          {!isCard && !isAi && !isDeleted && (
            <div className={`chat-message-hover-actions${showReactPicker ? ' is-pinned' : ''}`} role="toolbar" aria-label={tChat('chat.item.aria_hover_toolbar')}>
              <button type="button" className="chat-message-hover-action" onClick={() => setReplyingTo(message.roomId, message)} aria-label={tChat('chat.item.aria_reply')}>
                <Reply size={13} />
              </button>
              <button type="button" className="chat-message-hover-action" onClick={handleCopy} aria-label={copied ? '복사됨' : tChat('chat.item.aria_copy')}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
              <div className="chat-message-react-wrap">
                <button type="button" className="chat-message-hover-action" onClick={() => setShowReactPicker((v) => !v)} aria-label={tChat('chat.item.aria_emoji')} aria-expanded={showReactPicker}>
                  <Smile size={13} />
                </button>
                {showReactPicker && <EmojiPicker onSelect={handleReact} onClose={() => setShowReactPicker(false)} />}
              </div>
              {isMe && (
                <button type="button" className="chat-message-hover-action chat-message-hover-action--danger" onClick={handleDelete} aria-label={tChat('chat.item.aria_delete')}>
                  <Trash2 size={13} />
                </button>
              )}
              <button type="button" className="chat-message-hover-action" aria-label={tChat('chat.item.aria_more')} disabled>
                <MoreVertical size={13} />
              </button>
            </div>
          )}
        </div>
        )}

        {!isDeleted && <AttachmentList attachments={message.attachments} roomId={message.roomId} />}

        {/* chat-doc-preview — md 첨부/링크 '미리보기' 트리거 (우측 패널 오픈). additive. */}
        {!isDeleted && <MarkdownPreviewTriggers message={message} />}

        {!isCard && !isDeleted && <ReactionBar reactions={reactions} onToggle={handleReact} />}

        {/* chat-read-receipts — 내 메시지의 읽음 상태. 말풍선/첨부 아래 우측 정렬 한 줄.
          * 첨부만 있는 메시지(말풍선 없음)에도 동일하게 붙도록 body 의 마지막 자식으로 둔다.
          * unreadBy > 0 → 아직 안 읽은 인원 수(1:1 이면 항상 1), 0 → '읽음'/'모두 읽음'(마지막 메시지만). */}
        {unreadBy > 0 ? (
          <div className="chat-message-receipt" title={readTitle ?? undefined}>
            <span className="chat-message-receipt-count">{unreadBy}</span>
          </div>
        ) : readLabel ? (
          <div className="chat-message-receipt">
            <span className="chat-message-receipt-label">{readLabel}</span>
          </div>
        ) : null}

        {isCard && <AiActionCard message={message} />}

        {/* ★★★★ 승인 카드 — 「묻지도 않고 보냈다」(2026-09-21)를 막는 자리. */}
        {confirmCard && (
          <AiConfirmChatCard roomId={message.roomId} messageId={message.id} card={confirmCard} />
        )}
      </div>
    </div>
  );
}

